import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type {
  FilterWrapperParams,
  IAfterGuiAttachedParams,
} from 'ag-grid-community';
import type { CustomFilterDisplayProps } from 'ag-grid-react';
import { useGridFilterDisplay } from 'ag-grid-react';
import { CheckboxField, SearchField } from '@wk/components-v3-react16';
import { formatUserEmail } from '../../utils/formatUserEmail';
import { useGetAllStoppedBy } from './useGetAllStoppedBy';
import { useStoppedByFilter } from './useStoppedByFilter';
import { useUserSearch } from '../UserFilter/useUserSearch';
import type { StoppedByFilterModel } from './types';
import styles from './StoppedByFilter.module.scss';

interface StoppedByFilterProperties
  extends CustomFilterDisplayProps<StoppedByFilterModel>,
    FilterWrapperParams {}

const StoppedByFilter = ({
  model,
  onStateChange,
  state,
}: StoppedByFilterProperties) => {
  const searchInputReference = useRef<HTMLInputElement>(null);
  const selectAllInputReference = useRef<HTMLInputElement>(null);
  const { data: users, isLoading } = useGetAllStoppedBy();

  const afterGuiAttached = useCallback(
    (parameters?: IAfterGuiAttachedParams) => {
      if (!parameters?.suppressFocus) {
        searchInputReference.current?.focus();
      }
    },
    []
  );

  useGridFilterDisplay({ afterGuiAttached });
  useStoppedByFilter(model);

  const normalizedUsers = useMemo(
    () =>
      users?.map((u) => ({ userEmail: u.userEmail, userId: Number(u.userId) })),
    [users]
  );

  const { filteredUsers, searchTerm, setSearchTerm } =
    useUserSearch(normalizedUsers);

  const selectedUserIds: number[] = useMemo(
    () => state.model?.stoppedByUserIds || [],
    [state.model?.stoppedByUserIds]
  );

  const isAllFilteredSelected = useMemo(
    () =>
      filteredUsers.length > 0 &&
      filteredUsers.every((user) => selectedUserIds.includes(user.userId)),
    [filteredUsers, selectedUserIds]
  );

  const isIndeterminate = useMemo(() => {
    if (filteredUsers.length === 0) {
      return false;
    }
    const selectedCount = filteredUsers.filter((u) =>
      selectedUserIds.includes(u.userId)
    ).length;
    return selectedCount > 0 && !isAllFilteredSelected;
  }, [filteredUsers, selectedUserIds, isAllFilteredSelected]);

  const selectAllAriaChecked = useMemo<boolean | 'mixed'>(() => {
    if (isIndeterminate) {
      return 'mixed';
    }
    return isAllFilteredSelected;
  }, [isIndeterminate, isAllFilteredSelected]);

  useEffect(() => {
    if (selectAllInputReference.current) {
      selectAllInputReference.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const updateModel = useCallback(
    (userIds: number[]) => {
      const uniqueIds = Array.from(new Set(userIds));
      const newModel =
        uniqueIds.length > 0 ? { stoppedByUserIds: uniqueIds } : null;
      onStateChange({ model: newModel });
    },
    [onStateChange]
  );

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const userId = Number.parseInt(event.target.value, 10);
      const isSelected = selectedUserIds.includes(userId);
      const updatedIds = isSelected
        ? selectedUserIds.filter((id) => id !== userId)
        : [...selectedUserIds, userId];
      updateModel(updatedIds);
    },
    [selectedUserIds, updateModel]
  );

  const handleSelectAll = useCallback(() => {
    if (isAllFilteredSelected) {
      const filteredIdsSet = new Set(filteredUsers.map((u) => u.userId));
      const remainingIds = selectedUserIds.filter(
        (id) => !filteredIdsSet.has(id)
      );
      updateModel(remainingIds);
    } else {
      const filteredIds = filteredUsers.map((u) => u.userId);
      updateModel(Array.from(new Set([...selectedUserIds, ...filteredIds])));
    }
  }, [isAllFilteredSelected, filteredUsers, selectedUserIds, updateModel]);

  const handleKeydown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const spaceKeys = [' ', 'Space'];
      if (
        spaceKeys.includes(event.key) &&
        event.currentTarget.getAttribute('aria-disabled')
      ) {
        event.preventDefault();
      }
    },
    []
  );

  if (isLoading) {
    return <div className={styles.container}>Loading...</div>;
  }

  return (
    <div className={styles.container} tabIndex={-1}>
      <div className={styles.searchWrapper}>
        <SearchField
          searchButtonId="stopped-by-search-button"
          size="large"
          hasClearIcon={searchTerm.length > 0}
          onClear={() => setSearchTerm('')}
        >
          <input
            ref={searchInputReference}
            type="search"
            name="stopped-by-search"
            aria-labelledby="stopped-by-search-button"
            autoComplete="off"
            onInput={(event) =>
              setSearchTerm((event.target as HTMLInputElement).value)
            }
            placeholder="Search users..."
            value={searchTerm}
          />
        </SearchField>
      </div>

      {filteredUsers.length > 0 && (
        <div className={styles.selectAllWrapper}>
          <CheckboxField
            labelFor="select-all-stopped-by"
            label="Select All"
            size="extra-small"
          >
            <input
              ref={selectAllInputReference}
              id="select-all-stopped-by"
              name="SelectAll"
              value="select-all"
              type="checkbox"
              checked={isAllFilteredSelected}
              aria-checked={selectAllAriaChecked}
              onChange={handleSelectAll}
              onKeyDown={handleKeydown}
              flow-id="checkbox"
            />
          </CheckboxField>
        </div>
      )}

      <div className={styles.checkboxList}>
        {filteredUsers.length > 0 ? (
          filteredUsers.map((user) => {
            const isChecked = selectedUserIds.includes(user.userId);
            const inputId = `stopped-by-${user.userId}`;
            const formattedName = formatUserEmail(user.userEmail);
            return (
              <div key={user.userId} className={styles.checkboxWrapper}>
                <CheckboxField
                  labelFor={inputId}
                  label={formattedName}
                  size="extra-small"
                >
                  <input
                    id={inputId}
                    name={user.userEmail}
                    value={user.userId.toString()}
                    type="checkbox"
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                    onKeyDown={handleKeydown}
                    flow-id="checkbox"
                  />
                </CheckboxField>
              </div>
            );
          })
        ) : (
          <div className={styles.noResults}>No matches</div>
        )}
      </div>
    </div>
  );
};

export default StoppedByFilter;
